import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, authFetch } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { Compass, Loader2, ArrowRight, ArrowLeft, CheckCircle2, Globe, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const latamCountries = [
  'México', 'Argentina', 'Brasil', 'Chile', 'Colombia', 'Costa Rica', 'Ecuador',
  'El Salvador', 'Guatemala', 'Honduras', 'Nicaragua', 'Panamá', 'Paraguay',
  'Perú', 'República Dominicana', 'Uruguay', 'Venezuela', 'Otro',
];

const industries = [
  { value: 'tecnologia', label: 'Tecnología' },
  { value: 'finanzas', label: 'Finanzas' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'recursos_humanos', label: 'Recursos Humanos' },
  { value: 'ventas', label: 'Ventas' },
  { value: 'operaciones', label: 'Operaciones' },
  { value: 'legal', label: 'Legal' },
  { value: 'salud', label: 'Salud' },
  { value: 'educacion', label: 'Educación' },
  { value: 'manufactura', label: 'Manufactura' },
  { value: 'otro', label: 'Otro' },
];

const seniorities = [
  { value: 'junior', label: 'Junior (0-2 años)' },
  { value: 'mid', label: 'Mid-Level (3-5 años)' },
  { value: 'senior', label: 'Senior (6-10 años)' },
  { value: 'lead', label: 'Lead / Gerente (10+ años)' },
];

const goals = [
  { value: 'evaluar_oferta', label: 'Evaluar una oferta de trabajo' },
  { value: 'negociar_salario', label: 'Negociar mi salario actual' },
  { value: 'entender_derechos', label: 'Entender mis derechos laborales' },
  { value: 'calcular_liquidacion', label: 'Calcular mi liquidación' },
  { value: 'cambiar_empleo', label: 'Buscar cambiar de empleo' },
  { value: 'desarrollo', label: 'Desarrollo profesional general' },
];

const mexicanStates = [
  'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche', 'Chiapas',
  'Chihuahua', 'Ciudad de México', 'Coahuila', 'Colima', 'Durango', 'Guanajuato',
  'Guerrero', 'Hidalgo', 'Jalisco', 'México', 'Michoacán', 'Morelos', 'Nayarit',
  'Nuevo León', 'Oaxaca', 'Puebla', 'Querétaro', 'Quintana Roo', 'San Luis Potosí',
  'Sinaloa', 'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala', 'Veracruz', 'Yucatán', 'Zacatecas'
];

export default function OnboardingPage() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState(user?.email || '');
  const [waitlistCountry, setWaitlistCountry] = useState('');
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    country: '',
    state: '',
    city: '',
    preferred_language: 'es',
    role_title: '',
    industry: '',
    seniority: '',
    salary: '',
    main_goal: '',
  });

  const totalSteps = 3;
  const progress = (step / totalSteps) * 100;

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCountryChange = (value) => {
    updateField('country', value);
    if (value && value !== 'México') {
      setWaitlistCountry(value);
      setShowWaitlist(true);
    } else {
      setShowWaitlist(false);
    }
  };

  const handleWaitlistSubmit = async () => {
    if (!waitlistEmail) return;
    setWaitlistLoading(true);
    try {
      await fetch(`${API_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: waitlistEmail, country: waitlistCountry }),
      });
      setWaitlistSubmitted(true);
    } catch {
      toast.error('Error al registrar. Intenta de nuevo.');
    } finally {
      setWaitlistLoading(false);
    }
  };

  const canProceed = () => {
    if (step === 1) {
      return formData.name && formData.country === 'México' && formData.state && formData.city;
    }
    if (step === 2) {
      return formData.role_title && formData.industry && formData.seniority;
    }
    return formData.main_goal;
  };

  const handleNext = () => {
    if (canProceed()) {
      setStep((s) => Math.min(s + 1, totalSteps));
    }
  };

  const handleBack = () => {
    setStep((s) => Math.max(s - 1, 1));
  };

  const handleSubmit = async () => {
    if (!canProceed()) return;
    
    setLoading(true);
    try {
      const response = await authFetch(`${API_URL}/api/users/onboarding`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          salary: formData.salary ? parseFloat(formData.salary) : null,
        }),
      });

      if (!response.ok) {
        throw new Error('Error al guardar información');
      }

      const updatedUser = await response.json();
      updateUser(updatedUser);
      toast.success('¡Perfil completado!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <Compass className="w-8 h-8 text-[#2563EB]" />
          <span className="font-bold text-2xl tracking-tight font-['Manrope']">
            Klarvi
          </span>
        </div>

        <Card className="border shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-['Manrope']">
              {step === 1 && 'Información Personal'}
              {step === 2 && 'Tu Perfil Profesional'}
              {step === 3 && 'Tu Objetivo Principal'}
            </CardTitle>
            <CardDescription>
              Paso {step} de {totalSteps}
            </CardDescription>
            <Progress value={progress} className="mt-4" />
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Waitlist Screen for non-México countries */}
            {showWaitlist ? (
              <div className="text-center space-y-6 py-4 animate-fade-in">
                <div className="w-16 h-16 rounded-full bg-[#2563EB]/10 flex items-center justify-center mx-auto">
                  <Globe className="w-8 h-8 text-[#2563EB]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#0F172A] font-['Manrope']">
                    Klarvi opera actualmente en México
                  </h3>
                  <p className="mt-2 text-muted-foreground">
                    Estamos trabajando para llegar a más países de LATAM. Déjanos tu correo y te avisamos cuando lleguemos a tu país.
                  </p>
                </div>
                {waitlistSubmitted ? (
                  <div className="flex items-center justify-center gap-2 text-[#10B981] py-4" data-testid="waitlist-success">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-medium">¡Listo! Te avisaremos cuando Klarvi llegue a tu país.</span>
                  </div>
                ) : (
                  <div className="space-y-3 max-w-sm mx-auto">
                    <Input
                      type="email"
                      value={waitlistEmail}
                      onChange={(e) => setWaitlistEmail(e.target.value)}
                      placeholder="tu@correo.com"
                      data-testid="waitlist-email"
                    />
                    <Button
                      onClick={handleWaitlistSubmit}
                      disabled={!waitlistEmail || waitlistLoading}
                      className="w-full bg-[#2563EB] hover:bg-[#2563EB]/90"
                      data-testid="waitlist-submit"
                    >
                      {waitlistLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Mail className="w-4 h-4 mr-2" />
                      )}
                      Avísame
                    </Button>
                  </div>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setShowWaitlist(false); updateField('country', ''); }}
                  className="text-muted-foreground"
                  data-testid="waitlist-back"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Cambiar país
                </Button>
              </div>
            ) : (
            <>
            {/* Step 1: Personal Info */}
            {step === 1 && (
              <div className="space-y-4 animate-fade-in">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre Completo</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder="Tu nombre completo"
                    data-testid="onboarding-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">País</Label>
                  <Select
                    value={formData.country}
                    onValueChange={handleCountryChange}
                  >
                    <SelectTrigger data-testid="onboarding-country">
                      <SelectValue placeholder="Selecciona tu país" />
                    </SelectTrigger>
                    <SelectContent>
                      {latamCountries.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {formData.country === 'México' && (
                  <>
                <div className="space-y-2">
                  <Label htmlFor="state">Estado</Label>
                  <Select
                    value={formData.state}
                    onValueChange={(v) => updateField('state', v)}
                  >
                    <SelectTrigger data-testid="onboarding-state">
                      <SelectValue placeholder="Selecciona tu estado" />
                    </SelectTrigger>
                    <SelectContent>
                      {mexicanStates.map((state) => (
                        <SelectItem key={state} value={state}>
                          {state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">Ciudad</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => updateField('city', e.target.value)}
                    placeholder="Tu ciudad"
                    data-testid="onboarding-city"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="language">Idioma Preferido</Label>
                  <Select
                    value={formData.preferred_language}
                    onValueChange={(v) => updateField('preferred_language', v)}
                  >
                    <SelectTrigger data-testid="onboarding-language">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="es">Español</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                  </>
                )}
              </div>
            )}

            {/* Step 2: Professional Info */}
            {step === 2 && (
              <div className="space-y-4 animate-fade-in">
                <div className="space-y-2">
                  <Label htmlFor="role">Puesto Actual</Label>
                  <Input
                    id="role"
                    value={formData.role_title}
                    onChange={(e) => updateField('role_title', e.target.value)}
                    placeholder="Ej: Desarrollador de Software"
                    data-testid="onboarding-role"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="industry">Industria</Label>
                  <Select
                    value={formData.industry}
                    onValueChange={(v) => updateField('industry', v)}
                  >
                    <SelectTrigger data-testid="onboarding-industry">
                      <SelectValue placeholder="Selecciona tu industria" />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map((ind) => (
                        <SelectItem key={ind.value} value={ind.value}>
                          {ind.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="seniority">Nivel de Experiencia</Label>
                  <Select
                    value={formData.seniority}
                    onValueChange={(v) => updateField('seniority', v)}
                  >
                    <SelectTrigger data-testid="onboarding-seniority">
                      <SelectValue placeholder="Selecciona tu nivel" />
                    </SelectTrigger>
                    <SelectContent>
                      {seniorities.map((sen) => (
                        <SelectItem key={sen.value} value={sen.value}>
                          {sen.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="salary">Salario Mensual (MXN) - Opcional</Label>
                  <Input
                    id="salary"
                    type="number"
                    value={formData.salary}
                    onChange={(e) => updateField('salary', e.target.value)}
                    placeholder="Ej: 45000"
                    data-testid="onboarding-salary"
                  />
                </div>
              </div>
            )}

            {/* Step 3: Goal */}
            {step === 3 && (
              <div className="space-y-4 animate-fade-in">
                <Label>¿Cuál es tu objetivo principal?</Label>
                <div className="grid gap-3">
                  {goals.map((goal) => (
                    <button
                      key={goal.value}
                      type="button"
                      onClick={() => updateField('main_goal', goal.value)}
                      data-testid={`goal-${goal.value}`}
                      className={`flex items-center gap-3 p-4 rounded-lg border text-left transition-all
                        ${formData.main_goal === goal.value
                          ? 'border-[#2563EB] bg-[#2563EB]/5 text-[#2563EB]'
                          : 'border-border hover:border-[#2563EB]/50 hover:bg-muted'
                        }`}
                    >
                      <CheckCircle2
                        className={`w-5 h-5 flex-shrink-0 ${
                          formData.main_goal === goal.value
                            ? 'text-[#2563EB]'
                            : 'text-muted-foreground'
                        }`}
                      />
                      <span className="font-medium">{goal.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex gap-3 pt-4">
              {step > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBack}
                  className="flex-1"
                  data-testid="onboarding-back"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Atrás
                </Button>
              )}
              {step < totalSteps ? (
                <Button
                  type="button"
                  onClick={handleNext}
                  disabled={!canProceed()}
                  className="flex-1 bg-[#0F172A] hover:bg-[#0F172A]/90"
                  data-testid="onboarding-next"
                >
                  Siguiente
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canProceed() || loading}
                  className="flex-1 bg-[#2563EB] hover:bg-[#2563EB]/90"
                  data-testid="onboarding-submit"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  Completar
                </Button>
              )}
            </div>
            </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
